import { useMemo } from 'react';
import { useTranslation } from '../LanguageManagement/hooks/useTranslation';

/**
 * Formateadores de números, montos y fechas de MasterDocument, ligados al idioma
 * activo.
 *
 * El kit tenía 14 sitios con `new Intl.NumberFormat('es-CO', …)` y
 * `currency: 'COP'` a pelo, pese a que el adapter ya expone `locale` y `currency`
 * inyectables (`adapter/createDefaultAdapter.ts`). El resultado era que un host en
 * inglés seguía viendo separadores de miles colombianos.
 *
 * ## La moneda NO sigue al idioma
 *
 * Distinción deliberada, y es la parte fácil de equivocar: el **locale** decide
 * las convenciones de formato (separador de miles, orden de la fecha) y sí cambia
 * con el idioma; la **moneda** es un dato del documento y NO cambia nunca por
 * idioma. Un documento en COP se muestra en COP con la UI en inglés — cambia
 * `1.234,00` a `1,234.00`, no el código de moneda.
 *
 * ## Región por idioma
 *
 * `currentLanguage` es un código corto (`es`, `en`). Se mapea a un tag BCP 47 con
 * región para preservar el formato que el kit ya producía: `es` → `es-CO` mantiene
 * exactamente el render actual (punto como separador de miles), en vez del `es`
 * genérico.
 *
 * @see src/components/MasterDocument/adapter/createDefaultAdapter.ts - locale/currency del adapter
 */

/** Tag BCP 47 por código corto de idioma. Fuera del mapa se usa el código tal cual. */
const REGION_POR_IDIOMA: Record<string, string> = {
  es: 'es-CO',
  en: 'en-US',
};

/** Moneda por defecto del kit, igual que la del adapter. */
export const MONEDA_POR_DEFECTO = 'COP';

/** Resuelve el locale de formato a partir del código corto de idioma. */
export function resolveLocale(language: string | undefined): string {
  if (!language) return REGION_POR_IDIOMA.es;
  return REGION_POR_IDIOMA[language] ?? language;
}

/** Formateadores listos para usar en el render. */
export interface MasterDocumentFormatters {
  /** Locale BCP 47 efectivo, por si hace falta un `Intl` ad hoc. */
  locale: string;
/**
   * Monto con símbolo de moneda. Ver {@link createCurrencyFormatter} para la
   * regla: un monto entero se muestra con los decimales propios de la moneda y
   * uno con decimales nunca los pierde.
   */
  formatCurrency: (value: number) => string;
  /**
   * Alias de {@link MasterDocumentFormatters.formatCurrency}, conservado por
   * compatibilidad: es lo que consumen las celdas de la tabla de líneas.
   *
   * Tuvo una regla propia —dejar que el CLDR de la moneda fijara el máximo de
   * decimales, que para COP puede ser CERO— y por eso un precio de `33,33` se
   * pintaba `$ 33`. Ocultar decimales capturados no es "compacto", es incorrecto,
   * así que hoy las dos funciones son la misma.
   */
  formatCurrencyCompact: (value: number) => string;
  /** Número con 2 decimales. */
  formatAmount: (value: number) => string;
  /**
   * Tasa de cambio: mínimo 2 decimales y sin recortar la precisión adicional.
   *
   * Una TRM como `4185,567` debe mostrarse completa; `formatAmount` la redondearía
   * a 2 decimales y falsearía el dato.
   */
  formatRate: (value: number) => string;
  /** Número sin decimales forzados. */
  formatNumber: (value: number) => string;
  /** Fecha (`Date` o `YYYY-MM-DD`) en formato medio, sin desfase de zona horaria. */
  formatDate: (value: Date | string | undefined | null) => string;
  /** Fecha y hora en formato medio/corto. */
  formatDateTime: (value: Date | string | undefined | null) => string;
}

/**
 * Convierte `YYYY-MM-DD` a `Date` local.
 *
 * `new Date('2026-08-06')` se interpreta como UTC y en zonas al oeste de Greenwich
 * retrocede un día. Se construye con componentes para evitarlo.
 */
function parseDate(value: Date | string | undefined | null): Date | undefined {
  if (value == null || value === '') return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (soloFecha) {
    return new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * Formateador de moneda del kit: **nunca oculta decimales capturados**.
 *
 * ## El bug que cierra
 *
 * Los tres formateadores de moneda del kit pedían `minimumFractionDigits: 0` y
 * dejaban el MÁXIMO en manos del CLDR de la moneda. Para monedas que el CLDR
 * declara sin fracción —COP entre ellas, según la versión de ICU— ese máximo es
 * CERO, así que un precio capturado de `33,33` se pintaba `$ 33` y un neto de
 * `424,9575` se pintaba `$ 425`: la grilla de líneas y el widget de Totales
 * mostraban cifras que el usuario lee como un error de cálculo, no como formato.
 *
 * Peor: el resultado dependía de la versión de ICU del navegador (Node 22 resuelve
 * COP con 2 decimales; runtimes con datos más viejos, con 0), así que el mismo
 * documento se veía distinto en dos máquinas. Por eso aquí NO se deriva nada del
 * CLDR hacia abajo.
 *
 * ## La regla
 *
 * Siempre 2 decimales, o los de la moneda si usa más (BHD/KWD usan 3). Es la
 * convención contable y alinea las columnas de montos:
 * `$ 1.500,00` · `$ 33,33` · `$ 424,96` · `$ 505,70`.
 *
 * Redondear a 2 decimales PARA MOSTRAR es distinto de esconderlos todos: el valor
 * de `documentState` no se toca y las sumas siguen siendo exactas.
 */
export function createCurrencyFormatter(
  locale: string,
  currency: string = MONEDA_POR_DEFECTO,
): (value: number) => string {
  // El CLDR solo puede SUBIR el número de decimales, nunca bajarlo de 2.
  const delCldr = new Intl.NumberFormat(locale, { style: 'currency', currency })
    .resolvedOptions().maximumFractionDigits ?? 2;
  const decimales = Math.max(delCldr, 2);
  const fmt = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
  return (value) => fmt.format(value);
}

/**
 * Construye los formateadores para un locale y una moneda dados.
 *
 * Función pura, para los módulos que no pueden usar un hook. Los componentes
 * deberían usar {@link useMasterDocumentFormatters}.
 */
export function createFormatters(
  locale: string,
  currency: string = MONEDA_POR_DEFECTO,
): MasterDocumentFormatters {
  const currencyFmt = createCurrencyFormatter(locale, currency);
  const amountFmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Sin `maximumFractionDigits`: se deja el default de `Intl` (3 para decimales),
  // que es el comportamiento que ya tenía el panel de moneda.
  const rateFmt = new Intl.NumberFormat(locale, { minimumFractionDigits: 2 });
  const numberFmt = new Intl.NumberFormat(locale);
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
  const dateTimeFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  return {
    locale,
    formatCurrency: currencyFmt,
    formatCurrencyCompact: currencyFmt,
    formatAmount: (value) => amountFmt.format(value),
    formatRate: (value) => rateFmt.format(value),
    formatNumber: (value) => numberFmt.format(value),
    formatDate: (value) => {
      const date = parseDate(value);
      return date ? dateFmt.format(date) : '—';
    },
    formatDateTime: (value) => {
      const date = parseDate(value);
      return date ? dateTimeFmt.format(date) : '—';
    },
  };
}

/**
 * Formateadores del idioma activo.
 *
 * @param currency Código ISO de la moneda del documento. NO se deriva del idioma.
 *
 * @example
 * ```tsx
 * const { formatCurrency, formatDate } = useMasterDocumentFormatters();
 * <span>{formatCurrency(total)}</span>
 * ```
 */
export function useMasterDocumentFormatters(
  currency: string = MONEDA_POR_DEFECTO,
): MasterDocumentFormatters {
  const { currentLanguage } = useTranslation();
  const locale = resolveLocale(currentLanguage);
  return useMemo(() => createFormatters(locale, currency), [locale, currency]);
}
